import { Fragment } from 'react';
import { T, Var, useGT } from 'gt-react';
import { SplitHorizontalIcon } from '@phosphor-icons/react';
import type { APInvoiceLineItemSplitProposalAllocation } from '@erp/rpc';
import { Button } from '@/components/ui/button';
import { SystemEntityInline } from '@/components/system-entities';
import { PartKindLabel } from './PartKindLabel';
import {
  companionMembers,
  memberEntityType,
  primaryMember,
  type SplitProposalCard,
  type SplitProposalMember,
} from './splitProposalCardData';

type FormatMoney = (amount: string | null) => string;

function MemberLink({ member }: { member: SplitProposalMember }) {
  return (
    <SystemEntityInline
      entityType={memberEntityType(member)}
      entityId={member.docId}
    />
  );
}

function PartRow({
  part,
  money,
}: {
  part: APInvoiceLineItemSplitProposalAllocation;
  money: FormatMoney;
}) {
  const primary = primaryMember(part);
  const companions = companionMembers(part);
  return (
    <li className="flex flex-wrap items-center gap-x-2 text-sm">
      <span className="font-medium tabular-nums">{money(part.amount)}</span>
      <span aria-hidden>→</span>
      <span>
        <PartKindLabel kind={part.kind} />
      </span>
      {primary && <MemberLink member={primary} />}
      {companions.length > 0 && (
        <span className="flex flex-wrap items-center gap-x-1 text-xs text-muted-foreground">
          <T context="the same pick-up money also sits on these other documents">
            also on
          </T>
          {companions.map((companion) => (
            <Fragment key={companion.chargeId}>
              <MemberLink member={companion} />
            </Fragment>
          ))}
        </span>
      )}
    </li>
  );
}

interface SplitProposalCardsProps {
  cards: SplitProposalCard[];
  money: FormatMoney;
  /** Why the actions are off, or null when AP may accept and dismiss. */
  disabledReason: string | null;
  /** The line a request is in flight for. */
  pendingLineId: string | null;
  onAccept: (lineId: string) => void;
  onDismiss: (lineId: string) => void;
}

/**
 * Suggested splits of invoice lines, above the Related lines table. A
 * suggestion is already linked to its charges as tentative matches; AP
 * accepts it (the links become matches) or dismisses it.
 */
export function SplitProposalCards({
  cards,
  money,
  disabledReason,
  pendingLineId,
  onAccept,
  onDismiss,
}: SplitProposalCardsProps) {
  const gt = useGT();
  if (cards.length === 0) return null;

  return (
    <div className="space-y-2">
      {cards.map((card) => {
        const busy = pendingLineId === card.lineId;
        return (
          <section
            key={card.lineId}
            aria-label={gt('Suggested split for invoice line {line}', {
              line: String(card.lineNumber),
              context:
                'region label for a suggested split of an AP invoice line',
            })}
            className="flex flex-wrap items-start justify-between gap-3 rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-sky-950 dark:border-sky-900 dark:bg-sky-950 dark:text-sky-100"
          >
            <div className="min-w-0 space-y-1">
              <div className="flex items-center gap-2 text-sm font-medium">
                <SplitHorizontalIcon className="h-4 w-4 shrink-0" />
                {card.status === 'proposed' ? (
                  <T context="heading of a suggested split of an AP invoice line">
                    Suggested split for invoice line{' '}
                    <Var>{card.lineNumber}</Var> (
                    <Var>{money(card.lineAmount)}</Var>)
                  </T>
                ) : (
                  <T context="heading when several combinations of load/order/PO charges add up to an AP invoice line">
                    Invoice line <Var>{card.lineNumber}</Var> (
                    <Var>{money(card.lineAmount)}</Var>) could be split in
                    several ways
                  </T>
                )}
              </div>
              {card.status === 'proposed' ? (
                <ul className="space-y-0.5">
                  {card.parts.map((part) => (
                    <PartRow
                      key={part.members.map((m) => m.chargeId).join(':')}
                      part={part}
                      money={money}
                    />
                  ))}
                </ul>
              ) : (
                <div className="text-xs">
                  <T context="note on an ambiguous suggested split: nothing was matched">
                    Nothing was matched. Split the line by hand to choose.
                  </T>
                </div>
              )}
              <div className="text-xs text-muted-foreground">
                {card.reasoning}
              </div>
            </div>
            {card.status === 'proposed' && (
              <div className="flex shrink-0 gap-2">
                <Button
                  size="sm"
                  disabled={busy || disabledReason !== null}
                  title={disabledReason ?? undefined}
                  onClick={() => onAccept(card.lineId)}
                >
                  <T context="button that confirms a suggested split of an AP invoice line">
                    Accept split
                  </T>
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={busy || disabledReason !== null}
                  title={disabledReason ?? undefined}
                  onClick={() => onDismiss(card.lineId)}
                >
                  <T context="button that rejects a suggested split of an AP invoice line">
                    Dismiss
                  </T>
                </Button>
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}
